import { isEmpty } from 'lodash';
import { createSelector } from 'reselect';

import { REPORTS } from '../../../../js/constants';
import { getAllUniqueTaskIdsFromReport } from '../../../../js/helper/dashboardsHelpers';
import { getReportsStore } from '../selectors';

const DEFAULT_ARRAY = [];
const DEFAULT_OBJECT = {};

// ─── Root selectors (no reportId dependency — safe as singletons) ───

export const getReportsEntities = createSelector(
  getReportsStore,
  (reportsStore) => {
    const result = reportsStore?.entities;
    return isEmpty(result) ? DEFAULT_OBJECT : result;
  },
);

export const getSortedReportsIdList = createSelector(
  getReportsEntities,
  (reportsEntities) => {
    const result = Object.keys(reportsEntities)?.sort(
      (reportIdA, reportIdB) =>
        reportsEntities?.[reportIdA]?.position -
        reportsEntities?.[reportIdB]?.position,
    );
    return isEmpty(result) ? DEFAULT_ARRAY : result;
  },
);

const getReportId = (state, props) => props?.reportId;

// ─── Singleton selectors (safe for store.getState() one-off calls only) ───

export const getReportById = createSelector(
  [getReportsEntities, getReportId],
  (reportsEntities, reportId) => {
    const result = reportsEntities?.[reportId];
    return isEmpty(result) ? DEFAULT_OBJECT : result;
  },
);

export const getReportTitle = createSelector(
  getReportById,
  (report) => report?.title,
);

export const getReportData = createSelector(getReportById, (report) => {
  const result = report?.data;
  return isEmpty(result) ? DEFAULT_OBJECT : result;
});

export const getReportAggregatedData = createSelector(getReportData, (data) => {
  const result = data?.aggregated;
  return isEmpty(result) ? DEFAULT_ARRAY : result;
});

export const getReportQuery = createSelector(getReportById, (report) => {
  const result = report?.query;
  return isEmpty(result) ? DEFAULT_OBJECT : result;
});

export const getReportProjectIds = createSelector(
  getReportQuery,
  (query) => query?.projectIds,
);

export const getReportPortfolioIds = createSelector(
  getReportQuery,
  (query) => query?.portfolioIds,
);

export const getReportType = createSelector(
  getReportById,
  (report) => report?.type || 'bar',
);

export const getReportMeasure = createSelector(
  getReportById,
  (report) => report?.type || REPORTS.MEASURES.TASK_COUNT,
);

export const getReportUniqueTaskIds = createSelector(
  [
    getReportAggregatedData,
    createSelector(getReportData, (data) =>
      data?.groupBy?.includes('customFields'),
    ),
    createSelector(getReportData, (data) =>
      data?.stackBy?.includes('customFields'),
    ),
  ],
  (aggregated, groupedByCF, stackedByCF) => {
    const result = getAllUniqueTaskIdsFromReport(
      aggregated,
      groupedByCF,
      stackedByCF,
    );
    return isEmpty(result) ? DEFAULT_ARRAY : result;
  },
);

// The de-duplicated task total: one task can appear in several aggregated rows
// (assignee, tags, multi-select custom fields), so it is NOT sum(count). The
// server now ships it as a scalar, which is the only source available once the
// per-row taskIds stop being embedded (lite responses).
//
// The taskIds-derived union stays as the fallback and MUST NOT be removed: it
// still covers legacy/full responses (rollout flag off, or the server kill
// switch forcing the full shape), report shapes for which the server does not
// compute the scalar at all (it is undefined when there is no groupBy), and
// data already in the store from a build that predates the field.
const resolveTotalTasksCount = (data, aggregated, groupedByCF, stackedByCF) =>
  Number.isFinite(data?.uniqueTaskCount)
    ? data.uniqueTaskCount
    : getAllUniqueTaskIdsFromReport(aggregated, groupedByCF, stackedByCF)
        ?.length || 0;

export const getReportTotalTasksCount = createSelector(
  [
    getReportData,
    getReportAggregatedData,
    createSelector(getReportData, (data) =>
      data?.groupBy?.includes('customFields'),
    ),
    createSelector(getReportData, (data) =>
      data?.stackBy?.includes('customFields'),
    ),
  ],
  resolveTotalTasksCount,
);

// ─── Factory helper ───
// Creates a fully isolated selector chain per component instance.
// Every selector that depends on reportId MUST use this so that
// multiple mounted components with different reportIds don't bust
// each other's memoization cache.

const makeBaseSelectors = () => {
  const getReportByIdLocal = createSelector(
    [getReportsEntities, getReportId],
    (entities, reportId) => {
      const result = entities?.[reportId];
      return isEmpty(result) ? DEFAULT_OBJECT : result;
    },
  );

  const getReportDataLocal = createSelector(getReportByIdLocal, (report) => {
    const result = report?.data;
    return isEmpty(result) ? DEFAULT_OBJECT : result;
  });

  const getAggregatedLocal = createSelector(getReportDataLocal, (data) => {
    const result = data?.aggregated;
    return isEmpty(result) ? DEFAULT_ARRAY : result;
  });

  const isGroupByCFLocal = createSelector(
    getReportDataLocal,
    (data) => !!data?.groupBy?.includes('customFields'),
  );

  const isStackByCFLocal = createSelector(
    getReportDataLocal,
    (data) => !!data?.stackBy?.includes('customFields'),
  );

  return {
    getReportByIdLocal,
    getReportDataLocal,
    getAggregatedLocal,
    isGroupByCFLocal,
    isStackByCFLocal,
  };
};

// ─── Factory selectors (use these inside connect() with multiple instances) ───

export const makeGetReportById = () => {
  const { getReportByIdLocal } = makeBaseSelectors();
  return getReportByIdLocal;
};

export const makeGetReportTitle = () => {
  const { getReportByIdLocal } = makeBaseSelectors();
  return createSelector(getReportByIdLocal, (report) => report?.title);
};

export const makeGetReportData = () => {
  const { getReportDataLocal } = makeBaseSelectors();
  return createSelector(getReportDataLocal, (data) =>
    isEmpty(data) ? DEFAULT_OBJECT : data,
  );
};

export const makeGetReportAggregatedData = () => {
  const { getAggregatedLocal } = makeBaseSelectors();
  return createSelector(getAggregatedLocal, (data) =>
    isEmpty(data) ? DEFAULT_ARRAY : data,
  );
};

export const makeGetReportQuery = () => {
  const { getReportByIdLocal } = makeBaseSelectors();
  return createSelector(getReportByIdLocal, (report) => {
    const result = report?.query;
    return isEmpty(result) ? DEFAULT_OBJECT : result;
  });
};

export const makeGetReportProjectIds = () => {
  const { getReportByIdLocal } = makeBaseSelectors();
  const getQueryLocal = createSelector(
    getReportByIdLocal,
    (report) => report?.query ?? DEFAULT_OBJECT,
  );
  return createSelector(getQueryLocal, (query) => query?.projectIds);
};

export const makeGetReportPortfolioIds = () => {
  const { getReportByIdLocal } = makeBaseSelectors();
  const getQueryLocal = createSelector(
    getReportByIdLocal,
    (report) => report?.query ?? DEFAULT_OBJECT,
  );
  return createSelector(getQueryLocal, (query) => query?.portfolioIds);
};

export const makeGetReportType = () => {
  const { getReportByIdLocal } = makeBaseSelectors();
  return createSelector(getReportByIdLocal, (report) => report?.type || 'bar');
};

export const makeGetReportMeasure = () => {
  const { getReportByIdLocal } = makeBaseSelectors();
  return createSelector(
    getReportByIdLocal,
    (report) => report?.type || REPORTS.MEASURES.TASK_COUNT,
  );
};

export const makeGetReportUniqueTaskIds = () => {
  const { getAggregatedLocal, isGroupByCFLocal, isStackByCFLocal } =
    makeBaseSelectors();
  return createSelector(
    [getAggregatedLocal, isGroupByCFLocal, isStackByCFLocal],
    (aggregated, groupedByCF, stackedByCF) => {
      const result = getAllUniqueTaskIdsFromReport(
        aggregated,
        groupedByCF,
        stackedByCF,
      );
      return isEmpty(result) ? DEFAULT_ARRAY : result;
    },
  );
};

export const makeGetReportTotalTasksCount = () => {
  const {
    getReportDataLocal,
    getAggregatedLocal,
    isGroupByCFLocal,
    isStackByCFLocal,
  } = makeBaseSelectors();
  return createSelector(
    [
      getReportDataLocal,
      getAggregatedLocal,
      isGroupByCFLocal,
      isStackByCFLocal,
    ],
    resolveTotalTasksCount,
  );
};
